/**
 * Deterministic PRNG. Everything that "varies a little" in the generated world — building
 * heights, colour jitter — draws from here, seeded by the world seed mixed with the OSM
 * element id. That means the variation is stable across machines and runs, which is what
 * makes "same region + same seed → byte-identical world" hold.
 */

/** Mulberry32: tiny, fast, and good enough for cosmetic jitter. */
export function mulberry32(seed: number): () => number {
  let a = seed >>> 0;
  return function next() {
    a = (a + 0x6d2b79f5) >>> 0;
    let t = a;
    t = Math.imul(t ^ (t >>> 15), t | 1);
    t ^= t + Math.imul(t ^ (t >>> 7), t | 61);
    return ((t ^ (t >>> 14)) >>> 0) / 4294967296;
  };
}

/**
 * A single stable number in [0,1) for (seed, id). Used instead of a sequential stream so
 * that adding or removing an element never shifts the appearance of its neighbours.
 */
export function hashUnit(seed: number, id: number): number {
  let h = (seed ^ 0x9e3779b9) >>> 0;
  h = Math.imul(h ^ (id & 0xffff), 0x85ebca6b) >>> 0;
  h = Math.imul(h ^ (id >>> 16), 0xc2b2ae35) >>> 0;
  h = (h ^ (h >>> 13)) >>> 0;
  h = Math.imul(h, 0x27d4eb2f) >>> 0;
  // `^` yields a *signed* 32-bit int, so without this final `>>> 0` roughly half of all
  // ids came back negative — which indexed a palette array out of bounds and rendered
  // every second building pure black.
  h = (h ^ (h >>> 16)) >>> 0;
  return h / 4294967296;
}

/** FNV-1a over a string — used to build stable world ids and cache keys. */
export function hashString(str: string): number {
  let h = 0x811c9dc5;
  for (let i = 0; i < str.length; i++) {
    h ^= str.charCodeAt(i);
    h = Math.imul(h, 0x01000193) >>> 0;
  }
  return h >>> 0;
}

export function hashHex(str: string): string {
  return hashString(str).toString(16).padStart(8, '0');
}
